const coreUrl = document.querySelector('retained-scratch-gold')?.dataset.coreUrl || new URL('./retained-scratch-core.js', import.meta.url).href;
const { BRANDS, brandForShop, isPreview, readSuppression, triggerQualifies, liveConfigurationReason, subscribeEmail, createDirectLiveSession } = await import(coreUrl);

class RetainedScratchGold extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.listeners = new AbortController();
    this.queue = Promise.resolve();
    this.opened = false;
    this.closed = false;
    this.accepted = false;
    this.startTime = performance.now();
    this.lastY = scrollY;
    this.scrolled = false;
    this.run().catch(() => this.fail('initialization_failed'));
  }

  async run() {
    this.brand = this.dataset.brand;
    if (brandForShop(this.dataset.shop) !== this.brand) return this.fail('brand_mismatch');
    this.copy = JSON.parse(this.querySelector('[data-gold-copy]').textContent);
    this.preview = isPreview({ search: location.search, designMode: this.dataset.designMode === 'true', themeRole: this.dataset.themeRole });
    if (this.preview) return this.mount();
    this.deploymentMode = this.dataset.deploymentMode || 'native_ab';
    const reason = liveConfigurationReason({ enabled: this.dataset.enabled === 'true', integrationVerified: this.dataset.integrationVerified === 'true',
      shop: this.dataset.shop, brand: this.brand, coupon: this.dataset.coupon, hero: this.dataset.hero, deploymentMode: this.deploymentMode });
    if (reason) return this.fail(reason);
    if (this.dataset.pageType !== 'product' || /\/(cart|checkout|checkouts|account)(?:\/|$)/.test(location.pathname)) return this.fail('excluded_page');
    this.storage = window.localStorage;
    const initial = this.suppression();
    if (initial.knownSubscriber || initial.submitted || Date.now() - initial.dismissedAt < 7 * 86400000) return this.fail('suppressed');
    if (this.deploymentMode === 'direct_live') {
      this.adapter = { prepareSession: options => createDirectLiveSession(options) };
      this.dataset.measurementState = 'direct_live_no_ab_analytics';
    } else {
    const configRequest = new AbortController();
    const configTimeout = setTimeout(() => configRequest.abort(), 5000);
    let allConfig;
    try {
      const response = await fetch(this.dataset.intelligemsConfigUrl, { signal: configRequest.signal, credentials: 'omit' });
      if (!response.ok) return this.fail('experiment_configuration_unavailable');
      allConfig = await response.json();
    } finally { clearTimeout(configTimeout); }
    const native = await import(this.dataset.intelligemsUrl);
    if (!this.isConnected) return;
    this.native = native;
    this.adapter = native.createIntelligemsAdapter({ config: allConfig.brands?.[this.brand], windowRef: window, storage: this.storage });
    if (!await this.adapter.ready()) return this.fail(this.adapter.getReadinessReason());
    this.nativeConfig = allConfig.brands[this.brand];
    const assigned = window.igData.user.getTestGroup(this.nativeConfig.experimentId);
    if (assigned?.id === this.nativeConfig.groupIds.A) { this.dataset.displayState = 'control_unchanged'; return; }
    if (assigned?.id !== this.nativeConfig.groupIds.B) return this.fail('no_native_challenger_assignment');
    }
    if (!this.suppressLegacy()) return this.fail('legacy_popup_already_visible');
    this.legacyObserver = new MutationObserver(() => {
      if (!this.suppressLegacy()) this.fail('legacy_popup_already_visible');
    });
    this.legacyObserver.observe(document.body, { childList: true, subtree: true });
    this.dataset.displayState = 'waiting_for_scroll_and_delay';
    const check = () => this.checkTrigger();
    addEventListener('scroll', () => {
      if (Math.abs(scrollY - this.lastY) > 1) this.scrolled = true;
      this.lastY = scrollY;
      check();
    }, { passive: true, signal: this.listeners.signal });
    addEventListener('resize', check, { signal: this.listeners.signal });
    document.addEventListener('visibilitychange', check, { signal: this.listeners.signal });
    this.triggerTimer = setTimeout(check, Math.max(0, 8000 - (performance.now() - this.startTime)));
    check();
  }

  suppression() {
    return readSuppression({ brand: this.brand, storage: this.storage, pageType: this.dataset.pageType, knownSubscriber: this.dataset.subscribed === 'true' });
  }

  async checkTrigger() {
    if (this.opened || this.checking || this.closed) return;
    if (!triggerQualifies({ pageType: this.dataset.pageType, elapsedMs: performance.now() - this.startTime, scrolled: this.scrolled,
      scrollY, scrollHeight: document.documentElement.scrollHeight, viewportHeight: innerHeight, visible: document.visibilityState === 'visible' })) return;
    this.checking = true;
    try {
      const session = await this.adapter.prepareSession({ triggerReady: true, suppression: () => this.suppression(), mode: 'live' });
      if (!session.canRender) return this.fail(session.reason);
      if (this.otherModalIsVisible()) return this.fail('existing_modal_open');
      this.session = session;
      this.opened = true;
      if (session.variant === 'A') this.fail('native_assignment_changed');
      else if (session.variant === 'B') await this.mount();
      else this.fail('unknown_variant');
    } catch { this.fail('routing_failed'); }
    finally { this.checking = false; }
  }

  otherModalIsVisible() {
    return [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(node => {
      if (node === this.dialog || this.dialog?.contains(node)) return false;
      const box = node.getBoundingClientRect(), style = getComputedStyle(node);
      return box.width > 0 && box.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  suppressLegacy() {
    const selector = this.brand === 'varnholt' ? 'varnholt-ticket-b' : 'tbc-scratch-card';
    const nodes = [...document.querySelectorAll(selector)];
    for (const node of nodes) {
      const root = node.shadowRoot || node;
      const open = root.querySelector('dialog[open], .stage:not([hidden])');
      if (open && open.getBoundingClientRect().width > 0 && open.getBoundingClientRect().height > 0) return false;
    }
    for (const node of nodes) node.remove();
    return true;
  }

  async mount() {
    await import(this.dataset.componentUrl);
    if (!this.isConnected || this.closed) return;
    this.opened = true;
    const dialog = document.createElement('dialog');
    dialog.setAttribute('aria-label', this.copy.name + ' welcome offer');
    dialog.style.cssText = 'padding:0;border:0;margin:0;width:100vw;max-width:none;height:100dvh;max-height:none;overflow:hidden;background:#f0ece4;color:#254522;';
    const card = document.createElement('retained-gold-card');
    card.setAttribute('brand', this.brand);
    card.setAttribute('preview', String(this.preview));
    if (this.dataset.hero) card.setAttribute('hero', this.dataset.hero);
    card.copy = this.copy;
    card.style.cssText = 'display:block;width:100%;height:100%;';
    this.card = card;
    this.dialog = dialog;
    dialog.appendChild(card);
    document.body.appendChild(dialog);
    this.priorFocus = document.activeElement;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    this.dataset.displayState = this.preview ? 'non_sending_preview' : 'challenger_waiting_for_exposure';
    const on = (name, fn) => card.addEventListener(name, fn, { signal: this.listeners.signal });
    on('scratch-close', () => this.close());
    on('scratch-reveal', () => this.track('scratch_reveal'));
    on('scratch-stage', event => { if (event.detail.stage === 'email') this.track('email_view'); });
    on('scratch-submit', event => this.submit(event.detail));
    on('scratch-continue', () => {
      const apply = !this.preview && this.accepted && this.brand === 'varnholt';
      this.close();
      if (apply) location.assign('/discount/' + encodeURIComponent(this.dataset.coupon) + '?redirect=' + encodeURIComponent(location.pathname));
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal: this.listeners.signal });
    if (this.preview) {
      const note = document.createElement('div');
      note.textContent = this.copy.previewNote;
      note.style.cssText = 'position:fixed;bottom:8px;left:50%;transform:translateX(-50%);font:9px/1.2 Arial,sans-serif;letter-spacing:1px;background:#fffdf1;color:#33442c;padding:7px 11px;border-radius:4px;max-width:95%;text-align:center;pointer-events:none;z-index:20;';
      dialog.appendChild(note);
      card.shadowRoot.querySelector('.close').focus({ preventScroll: true });
    } else if (this.deploymentMode === 'direct_live') {
      card.inert = false;
      this.dataset.displayState = 'direct_live_visible';
      card.shadowRoot.querySelector('.close').focus({ preventScroll: true });
    } else {
      card.inert = true;
      this.stopExposure = this.native.observeIntelligemsExposure(dialog, this.session, {
        onExposure: () => {
          clearTimeout(this.exposureTimer);
          card.inert = false;
          this.dataset.displayState = 'challenger_visible';
          card.shadowRoot.querySelector('.close').focus({ preventScroll: true });
        },
        onMeasurementFailure: () => this.fail('measurement_unhealthy')
      });
      this.exposureTimer = setTimeout(() => this.fail('visible_event_not_accepted'), 5000);
    }
    card.redraw();
  }

  track(name) {
    if (this.preview || !this.session) return Promise.resolve();
    this.queue = this.queue.then(async () => {
      const result = await this.session.track(name);
      if (!result.accepted) this.dataset.measurementState = result.reason || 'event_not_queued';
      return result;
    }).catch(() => { this.dataset.measurementState = 'event_not_queued'; });
    return this.queue;
  }

  async submit(detail) {
    if (this.preview || !this.session?.productionAllowed || this.closed || this.submitting) return;
    this.submitting = true;
    this.submissionAbort = new AbortController();
    try {
      await this.queue;
      const result = await this.session.submit(() => subscribeEmail({ brand: this.brand, email: detail.email, signal: this.submissionAbort.signal }));
      if (!result.submitted) throw new Error('signup_not_accepted');
      this.accepted = true;
      try { this.storage.setItem(BRANDS[this.brand].submittedKey, this.brand === 'varnholt' ? '1' : String(Date.now())); }
      catch { this.dataset.measurementState = 'suppression_storage_failed'; }
      if (this.closed) return;
      detail.onSuccess(this.brand === 'varnholt' ? { code: this.dataset.coupon } : {});
      if (this.deploymentMode === 'native_ab' && !result.measurementQueued) this.dataset.measurementState = 'accepted_signup_not_queued';
      await this.track('offer_shown');
    } catch {
      if (!this.closed) detail.onError(this.copy.signupError);
    } finally {
      detail.email = '';
      this.submitting = false;
      this.submissionAbort = null;
    }
  }

  close({ recordDismissal = true } = {}) {
    if (this.closed) return;
    this.closed = true;
    this.submissionAbort?.abort();
    clearTimeout(this.triggerTimer);
    clearTimeout(this.exposureTimer);
    this.stopExposure?.();
    if (!this.preview && recordDismissal) {
      try { this.storage?.setItem(BRANDS[this.brand].dismissedKey, String(Date.now())); } catch {}
      void this.track('closed');
    }
    if (this.dialog) {
      this.dialog.close();
      this.dialog.remove();
      document.documentElement.style.overflow = this.previousOverflow;
      if (this.priorFocus?.isConnected) this.priorFocus.focus({ preventScroll: true });
    }
    this.legacyObserver?.disconnect();
    this.dataset.displayState = 'closed';
  }

  fail(reason) {
    if (this.dialog) this.close({ recordDismissal: false });
    this.legacyObserver?.disconnect();
    this.dataset.displayState = reason || 'unavailable';
    clearTimeout(this.triggerTimer);
    this.closed = true;
  }

  disconnectedCallback() {
    this.close({ recordDismissal: false });
    this.listeners?.abort();
  }
}

if (!customElements.get('retained-scratch-gold')) customElements.define('retained-scratch-gold', RetainedScratchGold);
